import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import MultiSelect from "components/SolsticeComponents/MultiSelect";
import DateFilterSelect from "components/DateFilterSelect";
import {
  getTransactionTypes,
  getTransactionStatuses,
  getTransactionManagers,
} from "redux/actions/transactionActions";
import { TYPE_VIEW } from "./homeFilters";
import "./dashboardFilters.css";

const CATEGORY = "Transaction";

const toOptions = (items, valueField, labelField) =>
  (items || []).map((item) => ({ value: item[valueField], label: item[labelField] }));

// Notice date / type-or-status / manager filters for the shared Home dashboard header -
// built from the same MultiSelect + DateFilterSelect components and the same
// getTransactionTypes / getTransactionStatuses / getTransactionManagers actions the
// Transaction list's FilterModal uses, so behavior matches exactly. This single filter
// row (per Figma) drives both the Pre-transaction and Transaction sections at once,
// using the "Transaction" category's options.
const DashboardFilters = ({ view, value, onChange }) => {
  const dispatch = useDispatch();
  const clientId = useSelector((state) => state.clients.selectedClientId);
  const [typeOptions, setTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);

  useEffect(() => {
    if (!clientId) return;

    dispatch(getTransactionTypes(clientId, CATEGORY)).then((res) => {
      setTypeOptions(toOptions(res?.payload?.data, "transactionTypeId", "transactionType"));
    });
    dispatch(getTransactionStatuses(clientId, CATEGORY)).then((res) => {
      setStatusOptions(toOptions(res?.payload?.data, "transactionStatusId", "transactionStatusName"));
    });
    dispatch(getTransactionManagers(clientId, CATEGORY)).then((res) => {
      setManagerOptions(
        toOptions(res?.payload?.data, "transactionManagerEmail", "transactionManagerEmail")
      );
    });
  }, [clientId, dispatch]);

  // The center filter shows the complementary dimension to whatever's on display -
  // Status while viewing By-type cards, Type while viewing By-status cards - fetched
  // eagerly above so switching tabs swaps instantly with no options-loading flicker.
  const isStatusCenter = view === TYPE_VIEW;
  const centerField = isStatusCenter ? "statusIds" : "typeIds";
  const centerLabel = isStatusCenter ? "Transaction status" : "Transaction type";
  const centerOptions = isStatusCenter ? statusOptions : typeOptions;

  // DateFilterSelect/MultiSelect are both styled at 100% of their own wrapper (they're
  // normally stacked full-width in FilterModal's drawer), so each needs a wrapper here
  // to sit in a row instead of claiming the whole row. Using grid (not flex) for the row
  // guarantees all three columns compute to the exact same width via one formula and the
  // gaps between them stay perfectly uniform. Fixed at 3 columns (not auto-fit) - auto-fit
  // needs the grid's own width to already be definite to know how many columns fit, but
  // here the grid is itself a flex item with a content-based width, so that's circular and
  // auto-fit collapses to a single column (verified: this is what broke it last time).
  // repeat(3, ...) has no such ambiguity: always 3 columns, shrinking together on narrow
  // viewports (down to 0 if truly needed) instead of wrapping or overflowing.
  const fieldSx = { minWidth: 0 };
  const multiSelectSx = { width: "100%", maxWidth: "304px" };

  return (
    <Box
      className="tme-dashboard-filters"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 304px))",
        gap: "24px",
        alignItems: "flex-start",
      }}
    >
      <Box sx={fieldSx}>
        <DateFilterSelect
          label="Notice date"
          value={value.noticeDate}
          onChange={(noticeDate) => onChange({ ...value, noticeDate })}
        />
      </Box>
      <Box sx={fieldSx}>
        <MultiSelect
          key={centerField}
          label={centerLabel}
          options={centerOptions}
          selected={centerOptions.filter((o) => value[centerField].includes(o.value))}
          onSelect={(selected) => onChange({ ...value, [centerField]: selected.map((s) => s.value) })}
          placeholder="Select"
          sx={multiSelectSx}
        />
      </Box>
      <Box sx={fieldSx}>
        <MultiSelect
          label="Transaction manager"
          options={managerOptions}
          selected={managerOptions.filter((o) => value.managerEmails.includes(o.value))}
          onSelect={(selected) => onChange({ ...value, managerEmails: selected.map((s) => s.value) })}
          placeholder="Select"
          sx={multiSelectSx}
        />
      </Box>
    </Box>
  );
};

export default DashboardFilters;
